import { __extends, __awaiter, __generator, __assign } from 'tslib';
import * as React from 'react';
import { useLocalization } from '@shared-ui/localization-context';
import { UitkPrimaryButton } from 'uitk-react-button';
import { UitkLayoutFlex, UitkLayoutFlexItem } from 'uitk-react-layout-flex';
import { UitkSheetTransition, UitkSheet, UitkSheetContent } from 'uitk-react-sheet';
import { debounce } from 'throttle-debounce';
import { classNames, liveAnnounce, isElementVisible } from 'uitk-react-utils';
import './adapters/ess-adapter.esm.js';
import './adapters/thirdparty-adapter-factory.esm.js';
import './adapters/thirdparty-adapter.esm.js';
import './adapters/google-adapter.esm.js';
import './adapters/mapbox-adapter.esm.js';
import { getSizeOfTypeaheadResults, filterTypeaheadResults, selectTypeaheadResultIndex } from './adapters/typeahead-results.esm.js';
import { UitkTypeaheadInput } from './uitk-typeahead-input.esm.js';
import { UitkTypeaheadResults } from './uitk-typeahead-results.esm.js';
import styles from './uitk-typeahead.css';

var getClassNameList = classNames.bind(styles);
var isValueEmpty = function (value) { return value === '' || (Array.isArray(value) && value.length === 0); };
var TypeaheadNavKeys;
(function (TypeaheadNavKeys) {
    TypeaheadNavKeys["UP"] = "UP";
    TypeaheadNavKeys["DOWN"] = "DOWN";
})(TypeaheadNavKeys || (TypeaheadNavKeys = {}));
var TypeaheadMenu = /** @class */ (function (_super) {
    __extends(TypeaheadMenu, _super);
    function TypeaheadMenu(props) {
        var _this = _super.call(this, props) || this;
        _this.toolbarInputRef = React.createRef();
        _this.keyPressed = false;
        _this.newAttr = -1;
        _this.typingTimeout = 0;
        var _a = _this.props, dialog = _a.dialog, term = _a.term, debounceRate = _a.debounceRate;
        _this.state = {
            activeSuggestion: -1,
            categorizedResults: [],
            // eslint-disable-next-line react/no-unused-state
            data: {},
            // eslint-disable-next-line react/no-unused-state
            menu: dialog,
            results: [],
            searchTerm: term,
            showResults: true
        };
        _this.handleChange = _this.handleChange.bind(_this);
        _this.handleKeyPress = _this.handleKeyPress.bind(_this);
        _this.handleKeyDown = _this.handleKeyDown.bind(_this);
        _this.handleKeyUp = _this.handleKeyUp.bind(_this);
        _this.handleFocus = _this.handleFocus.bind(_this);
        _this.handleResultItemSelect = _this.handleResultItemSelect.bind(_this);
        _this.focusInput = _this.focusInput.bind(_this);
        _this.announceResults = _this.announceResults.bind(_this);
        _this.onDismiss = _this.onDismiss.bind(_this);
        _this.updateResults = _this.updateResults.bind(_this);
        _this.updateResultsDebounced =
            debounceRate && debounceRate > 0 ? debounce(debounceRate, _this.updateResults) : _this.updateResults;
        _this.menuOpened = _this.menuOpened.bind(_this);
        _this.menuClosed = _this.menuClosed.bind(_this);
        _this.resetActiveSuggestion = _this.resetActiveSuggestion.bind(_this);
        _this.resultsDomRef = React.createRef();
        return _this;
    }
    TypeaheadMenu.prototype.componentDidUpdate = function (prevProps, prevState) {
        var _a = this.state, searchTerm = _a.searchTerm, results = _a.results, showResults = _a.showResults;
        var _b = this.props, label = _b.label, l10n = _b.l10n, value = _b.value, defaultTerm = _b.defaultTerm;
        var searchLen = results && results.length;
        var isTypeAheadFocused = this.isTypeAheadFocused();
        if (isValueEmpty(value) && isValueEmpty(searchTerm) && isTypeAheadFocused && !defaultTerm) {
            liveAnnounce(l10n.formatText("typeahead.announceResults.trigerLabel", label));
        }
        if (prevState.searchTerm === searchTerm &&
            !isValueEmpty(value) &&
            this.keyPressed === false &&
            isTypeAheadFocused) {
            liveAnnounce(l10n.formatText("typeahead.announceResults.showingResults", label, searchLen, searchTerm));
        }
        if (prevState.searchTerm !== searchTerm) {
            if (showResults) {
                // eslint-disable-next-line react/no-did-update-set-state
                this.setState({
                    results: TypeaheadMenu.placeholder
                });
                this.updateResultsDebounced(searchTerm);
            }
        }
        this.focusInput();
    };
    TypeaheadMenu.prototype.handleFocus = function () {
        this.selectInput();
    };
    TypeaheadMenu.prototype.handleResultItemSelect = function (term, data, results) {
        var _this = this;
        var _a;
        var _b = this.props, adapter = _b.adapter, isMulti = _b.isMulti, onResultItemSelect = _b.onResultItemSelect, label = _b.label, l10n = _b.l10n;
        var searchTerm = this.state.searchTerm;
        return (_a = adapter
            .onItemSelection) === null || _a === void 0 ? void 0 : _a.call(adapter, data).then(function (result) {
            if (isMulti) {
                _this.setState({
                    activeSuggestion: -1
                });
            }
            else {
                _this.setState({
                    activeSuggestion: -1,
                    searchTerm: term,
                    showResults: false
                });
            }
            onResultItemSelect(term, result);
            // Results is the results prior to us triggering onResulItemSelect so we subtract 1
            // This is technically not correct because the typeahead is controlled and users could decide to not select the value (or do any other logic) in onChange even if that would not make sense.  Ideally liveAnnnounce logic would trigger based on state change and not on events
            var searchLen = results ? getSizeOfTypeaheadResults(results) - 1 : 0;
            if (isMulti) {
                liveAnnounce("".concat(l10n.formatText("typeahead.announceResults.itemSelected", term), "\n             ").concat(l10n.formatText('typeahead.announceResults.showingResults', label, searchLen, searchTerm)));
            }
            if (!isMulti) {
                setTimeout(function () {
                    liveAnnounce("".concat(l10n.formatText("typeahead.announceResults.itemSelected", term), " \n            ").concat(l10n.formatText('typeahead.announceResults.menuClosed')));
                }, 100);
                _this.menuClosed();
            }
        }).catch(function () {
            _this.updateResults(term, true, term !== '');
            _this.menuClosed();
        });
    };
    TypeaheadMenu.prototype.handleAnnounceResults = function (results) {
        var _this = this;
        if (this.typingTimeout) {
            clearTimeout(this.typingTimeout);
        }
        // Delay by 1 second
        // to ensure live announce is heard
        if ((results === null || results === void 0 ? void 0 : results.length) > 0) {
            this.typingTimeout = setTimeout(function () {
                _this.announceResults(results);
            }, 1000);
        }
    };
    TypeaheadMenu.prototype.handleKeyPress = function (event) {
        var _a = this.state, activeSuggestion = _a.activeSuggestion, searchTerm = _a.searchTerm;
        var _b = this.props, value = _b.value, l10n = _b.l10n;
        this.keyPressed = true;
        var resultsToShow = filterTypeaheadResults(this.getResultsToShow(), Array.isArray(value) ? value : []);
        var searchLen = resultsToShow && getSizeOfTypeaheadResults(resultsToShow);
        this.keyPressed = true;
        var searchChars = searchTerm && searchTerm.split('');
        if (searchChars) {
            var outputWithQuotes = "'".concat(searchChars.join("' , '"), " '");
            if (searchTerm !== '' && searchLen > 0) {
                liveAnnounce(l10n.formatText("typeahead.announceResults.resultsAvailable", outputWithQuotes, searchLen));
            }
        }
        if (event.key === 'Enter' || event.keyCode === 38) {
            event.preventDefault();
        }
        if (event.key === 'Enter') {
            var selected = selectTypeaheadResultIndex(resultsToShow, activeSuggestion);
            selected
                ? this.handleResultItemSelect(selected.value, selected.data, resultsToShow)
                : this.handleResultItemSelect(searchTerm, undefined, resultsToShow);
        }
    };
    TypeaheadMenu.prototype.handleChange = function (event) {
        var onInputChange = this.props.onInputChange;
        this.setState({ searchTerm: event.target.value, showResults: true }, this.focusInput);
        // To reset activeSuggestion when value is cleared
        if (event.target.value === '') {
            this.resetActiveSuggestion();
        }
        onInputChange && onInputChange(event);
    };
    TypeaheadMenu.prototype.handleActiveSuggestion = function (direction) {
        var _a = this.state, activeSuggestion = _a.activeSuggestion, results = _a.results, searchTerm = _a.searchTerm;
        var allowExactMatch = this.props.allowExactMatch;
        // Extra list item in case of allowExactMatch true
        var extraListItemForSearch = allowExactMatch && searchTerm ? 1 : 0;
        var resultsLength = results.length + extraListItemForSearch;
        var circularListFactor = 0;
        if (direction === TypeaheadNavKeys.UP) {
            circularListFactor = activeSuggestion === -1 ? resultsLength : resultsLength - 1;
        }
        else if (direction === TypeaheadNavKeys.DOWN) {
            circularListFactor = resultsLength + 1;
        }
        var activePosition = (activeSuggestion + circularListFactor) % resultsLength;
        var activePositionTab = (this.newAttr + circularListFactor) % resultsLength;
        this.setState({
            activeSuggestion: this.newAttr > -1 ? activePositionTab : activePosition
        });
        this.newAttr = -1;
    };
    TypeaheadMenu.prototype.handleUpArrowKey = function (e) {
        if (e.keyCode === 38) {
            e.preventDefault();
            this.handleActiveSuggestion(TypeaheadNavKeys.UP);
        }
    };
    TypeaheadMenu.prototype.handleDownArrowKey = function (e) {
        if (e.keyCode === 40) {
            this.keyPressed = true;
            this.handleActiveSuggestion(TypeaheadNavKeys.DOWN);
        }
    };
    TypeaheadMenu.prototype.handleTabKey = function (e) {
        if (e.keyCode === 9 || (e.shiftKey && e.keyCode === 9)) {
            var attr = e.target.parentElement.getAttribute('data-index');
            if (attr) {
                this.newAttr = Number(attr);
            }
            else {
                this.newAttr = -1;
            }
        }
    };
    TypeaheadMenu.prototype.handleEscKey = function (e) {
        if (e.keyCode === 27) {
            this.onDismiss();
        }
    };
    TypeaheadMenu.prototype.handleKeyDown = function (e) {
        this.handleDownArrowKey(e);
        this.handleUpArrowKey(e);
        this.handleEscKey(e);
    };
    TypeaheadMenu.prototype.handleKeyUp = function (e) {
        this.handleTabKey(e);
    };
    TypeaheadMenu.prototype.onDismiss = function () {
        var _a = this.props, isMulti = _a.isMulti, onDismiss = _a.onDismiss, l10n = _a.l10n;
        this.keyPressed = false;
        if (this.resultsDomRef.current) {
            this.resultsDomRef.current.scrollTop = 0;
        }
        if (onDismiss) {
            onDismiss();
        }
        if (isMulti) {
            liveAnnounce(l10n.formatText('typeahead.announceResults.menuClosed'));
        }
    };
    TypeaheadMenu.prototype.getResultsToShow = function () {
        var categorize = this.props.categorize;
        var _a = this.state, categorizedResults = _a.categorizedResults, results = _a.results;
        return categorize || (categorizedResults && categorizedResults.length)
            ? {
                items: categorizedResults,
                type: 'categorized'
            }
            : {
                items: results,
                type: 'uncategorized'
            };
    };
    TypeaheadMenu.getDerivedStateFromProps = function (nextProps, prevState) {
        var term = nextProps.term !== prevState.searchTerm && !nextProps.dialog ? nextProps.term : prevState.searchTerm;
        if (!nextProps.isMulti) {
            return {
                searchTerm: term
            };
        }
        return null;
    };
    TypeaheadMenu.prototype.menuClosed = function () {
        var _a = this.props, adapter = _a.adapter, onDismiss = _a.onDismiss;
        if (adapter && typeof adapter.logTypeaheadDismiss === 'function') {
            adapter.logTypeaheadDismiss();
        }
        if (onDismiss) {
            onDismiss();
        }
        // To reset activeSuggestion when menu is closed
        this.resetActiveSuggestion();
    };
    TypeaheadMenu.prototype.menuOpened = function () {
        var defaultTerm = this.props.defaultTerm;
        var searchTerm = this.state.searchTerm;
        if (this.resultsDomRef.current) {
            this.resultsDomRef.current.scrollTop = 0;
        }
        this.selectInput();
        this.updateResults(!searchTerm && defaultTerm ? defaultTerm : searchTerm, false, searchTerm !== '');
    };
    TypeaheadMenu.prototype.announceResults = function (results) {
        var searchTerm = this.state.searchTerm;
        var _a = this.props, l10n = _a.l10n, label = _a.label, defaultTerm = _a.defaultTerm;
        var searchChars = searchTerm && searchTerm.split('');
        if (searchChars) {
            var outputWithQuotes = "'".concat(searchChars.join("' , '"), " '");
            if (results && this.isTypeAheadFocused()) {
                liveAnnounce(l10n.formatText("typeahead.announceResults.resultsAvailable", outputWithQuotes, results.length));
            }
        }
        else if (defaultTerm) {
            if (results && this.isTypeAheadFocused()) {
                liveAnnounce(l10n.formatText("typeahead.announceResults.showingResultsDefault", label, results.length));
            }
        }
    };
    TypeaheadMenu.prototype.isCategorizedResult = function (result) {
        return 'heading' in result;
    };
    TypeaheadMenu.prototype.updateResults = function (term, makeRecentHistoryCall, makeAltDestCall) {
        return __awaiter(this, void 0, void 0, function () {
            var _a, adapter, categorize, defaultTerm, results, categorizedResults, queryResults, recentHistoryResults, termOrDefault, _b, tempResults, tempResults, allResults;
            var _c;
            var _this = this;
            return __generator(this, function (_d) {
                switch (_d.label) {
                    case 0:
                        this.lastSearchTerm = term;
                        _a = this.props, adapter = _a.adapter, categorize = _a.categorize, defaultTerm = _a.defaultTerm;
                        termOrDefault = term === '' ? defaultTerm !== null && defaultTerm !== void 0 ? defaultTerm : '' : term;
                        if (!(termOrDefault !== '' && !!makeRecentHistoryCall)) return [3 /*break*/, 5];
                        if (!categorize) return [3 /*break*/, 2];
                        return [4 /*yield*/, Promise.all([
                                adapter.fetchResultsCategorized(termOrDefault),
                                adapter.fetchRecentSearchResultsCategorized()
                            ])];
                    case 1:
                        _b = _d.sent();
                        return [3 /*break*/, 4];
                    case 2: return [4 /*yield*/, Promise.all([adapter.fetchResults(termOrDefault), adapter.fetchRecentSearchResults()])];
                    case 3:
                        _b = _d.sent();
                        _d.label = 4;
                    case 4:
                        _c = _b, queryResults = _c[0], recentHistoryResults = _c[1];
                        if (recentHistoryResults && recentHistoryResults.length > 0) {
                            categorize || this.isCategorizedResult(recentHistoryResults[0])
                                ? (categorizedResults = recentHistoryResults)
                                : (results = recentHistoryResults);
                        }
                        else {
                            categorize ? (categorizedResults = queryResults) : (results = queryResults);
                        }
                        return [3 /*break*/, 11];
                    case 5:
                        if (!categorize) return [3 /*break*/, 7];
                        return [4 /*yield*/, adapter.fetchResultsCategorized(termOrDefault)];
                    case 6:
                        categorizedResults = _d.sent();
                        return [3 /*break*/, 11];
                    case 7:
                        if (!(termOrDefault !== '' && !!makeAltDestCall)) return [3 /*break*/, 9];
                        return [4 /*yield*/, adapter.fetchAlternativeResults(termOrDefault)];
                    case 8:
                        tempResults = _d.sent();
                        tempResults && tempResults.length && this.isCategorizedResult(tempResults[0])
                            ? (categorizedResults = tempResults)
                            : (results = tempResults);
                        return [3 /*break*/, 11];
                    case 9: return [4 /*yield*/, adapter.fetchResults(termOrDefault)];
                    case 10:
                        tempResults = _d.sent();
                        tempResults && tempResults.length && this.isCategorizedResult(tempResults[0])
                            ? (categorizedResults = tempResults)
                            : (results = tempResults);
                        _d.label = 11;
                    case 11:
                        if (this.lastSearchTerm !== term) {
                            return [2 /*return*/];
                        }
                        if (results && results.length) {
                            adapter.logSuggestionShown(makeRecentHistoryCall || makeAltDestCall ? '' : term, results);
                            this.setState({ results: results, categorizedResults: [] }, function () {
                                _this.handleAnnounceResults(results);
                            });
                        }
                        else if (categorizedResults && categorizedResults.length) {
                            adapter.logSuggestionShown(makeRecentHistoryCall || makeAltDestCall ? '' : term, categorizedResults);
                            allResults = categorizedResults.flatMap(function (result) { return result.typeaheadResults; });
                            this.setState({ categorizedResults: categorizedResults, results: allResults }, function () {
                                _this.handleAnnounceResults(results);
                            });
                        }
                        else {
                            this.setState({ results: [], categorizedResults: [] });
                        }
                        return [2 /*return*/];
                }
            });
        });
    };
    TypeaheadMenu.prototype.selectInput = function () {
        var toolbarInputRef = this.toolbarInputRef;
        if (toolbarInputRef && toolbarInputRef.current && typeof toolbarInputRef.current.select === 'function') {
            toolbarInputRef.current.select();
        }
    };
    TypeaheadMenu.prototype.focusInput = function () {
        var toolbarInputRef = this.toolbarInputRef;
        if (toolbarInputRef &&
            toolbarInputRef.current &&
            typeof toolbarInputRef.current.focus === 'function' &&
            isElementVisible(this.toolbarInputRef.current)) {
            toolbarInputRef.current.focus();
        }
    };
    TypeaheadMenu.prototype.resetActiveSuggestion = function () {
        this.setState({
            activeSuggestion: -1
        });
    };
    TypeaheadMenu.prototype.isTypeAheadFocused = function () {
        return this.toolbarInputRef.current === document.activeElement;
    };
    TypeaheadMenu.prototype.render = function () {
        var _a = this.props, allowExactMatch = _a.allowExactMatch, children = _a.children, dialog = _a.dialog, stid = _a.stid, label = _a.label, listTitle = _a.listTitle, placeholder = _a.placeholder, handleClear = _a.handleClear, emptyResultPlaceholder = _a.emptyResultPlaceholder, dismissButtonText = _a.dismissButtonText, value = _a.value, isMulti = _a.isMulti, clearAllText = _a.clearAllText, onClearAll = _a.onClearAll, onItemRemove = _a.onItemRemove, selectedItemHeading = _a.selectedItemHeading, typeaheadInputRef = _a.typeaheadInputRef, historyDetail = _a.historyDetail, required = _a.required;
        var _b = this.state, activeSuggestion = _b.activeSuggestion, searchTerm = _b.searchTerm;
        var resultsToShow = this.getResultsToShow();
        return (React.createElement("div", { className: getClassNameList('uitk-menu-typeahead-wrapper') },
            React.createElement("div", { className: getClassNameList('uitk-typeahead-menu'), id: stid && "".concat(stid, "-menu") },
                React.createElement("div", { className: getClassNameList('uitk-menu-trigger') }, children),
                React.createElement(UitkSheetTransition, { isVisible: dialog, openCallback: this.menuOpened },
                    React.createElement(UitkSheet, { autoFocus: false, type: "popover", className: getClassNameList('uitk-typeahead-menu-container', {
                            'is-multi': isMulti
                        }), disableArrowKeyHandler: true, isVisible: dialog, onDismiss: this.menuClosed, triggerRef: typeaheadInputRef, showOverTrigger: true, autoPosition: false },
                        React.createElement(UitkLayoutFlex, { direction: "column", blockSize: "full_size" },
                            React.createElement(UitkLayoutFlexItem, null,
                                React.createElement("div", { className: getClassNameList('uitk-typeahead-input-wrapper') },
                                    React.createElement(UitkTypeaheadInput, { className: getClassNameList('uitk-typeahead-input-v2'), "data-stid": stid && "".concat(stid, "-menu-input"), domRef: this.toolbarInputRef, handleClear: handleClear, hasClearButton: searchTerm.length > 0, hasFloatedLabel: false, hasVisibleLabel: false, id: stid, label: label, onChange: this.handleChange, onKeyPress: this.handleKeyPress, onKeyDown: this.handleKeyDown, onKeyUp: this.handleKeyUp, placeholder: placeholder, required: required, value: searchTerm }))),
                            React.createElement(UitkLayoutFlexItem, null,
                                React.createElement(UitkSheetContent, { className: getClassNameList('uitk-typeahead-menu-content'), padded: false },
                                    React.createElement(UitkTypeaheadResults, { typeaheadLabel: label, allowExactMatch: allowExactMatch, onResultItemSelect: this.handleResultItemSelect, results: resultsToShow, searchTerm: searchTerm, stid: stid, listTitle: listTitle, activeSuggestion: activeSuggestion, emptyResultPlaceholder: emptyResultPlaceholder, isMulti: isMulti, dismissButtonText: dismissButtonText, selectedItemHeading: selectedItemHeading, clearAllText: clearAllText, onClearAll: onClearAll, onItemRemove: onItemRemove, value: value, domRef: this.resultsDomRef, historyDetail: historyDetail }))),
                            isMulti && (React.createElement(UitkLayoutFlexItem, null,
                                React.createElement(UitkLayoutFlex, { justifyContent: "end", className: getClassNameList('uitk-typeahead-menu-footer') },
                                    React.createElement(UitkLayoutFlexItem, null,
                                        React.createElement(UitkPrimaryButton, { disabled: (value === null || value === void 0 ? void 0 : value.length) === 0, onClick: this.onDismiss }, dismissButtonText)))))))))));
    };
    TypeaheadMenu.placeholder = [
        { disabled: true, icon: 'brightness_1', name: ' ', value: ' ', title: ' ' }
    ];
    return TypeaheadMenu;
}(React.Component));
var UitkTypeaheadMenu = function (props) {
    var l10n = useLocalization();
    return React.createElement(TypeaheadMenu, __assign({ l10n: l10n }, props));
};

export { TypeaheadMenu, UitkTypeaheadMenu };
